import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { jobsApi } from '../api/jobs'
import type { JobStatus, JobSummary, Paged } from '../api/types'
import { useAuth } from '../auth/useAuth'
import { can } from '../auth/AuthContext'
import { BbIcon } from '../components/BbIcon'
import type { BbIconName } from '../components/BbIcon'
import { EmptyState, ErrorState, LoadingState } from '../components/States'
import { useAsync } from '../hooks/useAsync'
import { formatInt, formatRelative } from '../lib/format'

/* Dashboard ported from prototypes/batchbooks_modernized_dashboard.

   The four tiles stay gated by capability, as they were before the port — the
   prototype shows all four unconditionally, but rendering an action a role
   cannot perform just produces a 403 on click.

   Recent Activity uses the prototype's four columns. Entity, company, row counts
   and credits used are no longer on this screen; they remain on /app/jobs and on
   each job's detail page. */

function greeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 18) return 'Good afternoon'
  return 'Good evening'
}

const TILES: Array<{
  tone: string
  icon: BbIconName
  title: string
  body: string
  path: string
  capability: 'import' | 'export' | 'delete'
}> = [
  {
    tone: 'bb-tile-import',
    icon: 'upload',
    title: 'Import',
    body: 'Upload Excel/CSV and import transactions in seconds.',
    path: '/app/import',
    capability: 'import',
  },
  {
    tone: 'bb-tile-export',
    icon: 'download',
    title: 'Export',
    body: 'Download transactions and lists for analysis.',
    path: '/app/export',
    capability: 'export',
  },
  {
    tone: 'bb-tile-delete',
    icon: 'trash',
    title: 'Delete',
    body: 'Safely remove bulk entries or duplicates.',
    path: '/app/delete',
    capability: 'delete',
  },
  {
    tone: 'bb-tile-modify',
    icon: 'edit-note',
    title: 'Modify',
    body: 'Bulk update or edit existing transactions with ease.',
    path: '/app/modify',
    capability: 'import',
  },
]

/** Maps the seven JobStatus values onto the prototype's three pill treatments. */
function statusPill(status: JobStatus): { cls: string; label: string } {
  switch (status) {
    case 'Completed':
      return { cls: 'bb-apill-ok', label: 'Completed' }
    case 'Running':
      return { cls: 'bb-apill-run', label: 'Running' }
    case 'Pending':
      return { cls: 'bb-apill-idle', label: 'Pending' }
    case 'PartialSuccess':
      return { cls: 'bb-apill-warn', label: 'Partial Success' }
    case 'Failed':
      return { cls: 'bb-apill-err', label: 'Failed' }
    case 'Cancelled':
      return { cls: 'bb-apill-idle', label: 'Cancelled' }
    case 'CancellationRequested':
      return { cls: 'bb-apill-idle', label: 'Cancelling' }
  }
}

/** The prototype's contextual action column: View Results / Download / Fix Errors. */
function RowAction({ job }: { job: JobSummary }) {
  if (job.status === 'Running' || job.status === 'Pending' || job.status === 'CancellationRequested') {
    return (
      <span className="bb-alink bb-alink-off" aria-disabled="true">
        Download
      </span>
    )
  }
  if (job.status === 'PartialSuccess' || job.status === 'Failed') {
    return (
      <Link className="bb-alink bb-alink-warn" to={`/app/jobs/${job.id}`}>
        Fix Errors
      </Link>
    )
  }
  return (
    <Link className="bb-alink" to={`/app/jobs/${job.id}`}>
      View Results
    </Link>
  )
}

function RecentActivity() {
  const { data, loading, error, reload } = useAsync<Paged<JobSummary>>(
    () => jobsApi.list({ pageSize: 5 }),
    [],
  )
  const rows = data?.items ?? []

  return (
    <section>
      <div className="bb-activity-head">
        <h2>Recent Activity</h2>
        <Link className="bb-viewall" to="/app/jobs">
          View All
        </Link>
      </div>

      <div className="bb-activity">
        {loading && rows.length === 0 ? (
          <LoadingState label="Loading your jobs…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon="↓"
            title="No jobs yet"
            body="Once you run your first import, export or delete it will show up here with its result."
            action={
              <Link className="bb-btn bb-btn-green" to="/app/import">
                Start an import
              </Link>
            }
          />
        ) : (
          <table className="bb-atable">
            <thead>
              <tr>
                <th>Job Details</th>
                <th>Status</th>
                <th>Time</th>
                <th className="bb-a-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((job) => {
                const pill = statusPill(job.status)
                return (
                  <tr key={job.id}>
                    <td>
                      {job.type} — {job.entityType}
                      {job.totalRows > 0 ? ` (${formatInt(job.totalRows)} lines)` : ''}
                    </td>
                    <td>
                      <span className={`bb-apill ${pill.cls}`}>
                        <i />
                        {pill.label}
                      </span>
                    </td>
                    <td className="bb-a-time">{formatRelative(job.finishedAt ?? job.createdAt)}</td>
                    <td className="bb-a-right">
                      <RowAction job={job} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  )
}

export function HomePage() {
  const { user } = useAuth()
  const firstName = useMemo(() => (user?.name ?? '').split(' ')[0] || 'there', [user?.name])
  const tiles = TILES.filter((tile) => can(user?.role, tile.capability))

  return (
    <div className="bb bb-dash">
      <section className="bb-dash-hi">
        <h1>
          {greeting()}, {firstName}
        </h1>
        <p>
          Do the numbers dance for you? We can set them right for you.
          <br />
          Let&rsquo;s get started!
        </p>
      </section>

      {/* The whole card is the action now — no "Start" button. A Link (not a
          div with onClick) keeps it a real navigable control: focusable,
          announced as a link, and openable with Enter. */}
      <section className="bb-tiles">
        {tiles.map((tile) => (
          <Link className={`bb-tile ${tile.tone}`} to={tile.path} key={tile.title}>
            <span className="bb-tile-ico">
              <BbIcon name={tile.icon} />
            </span>
            <h2>{tile.title}</h2>
            <p>{tile.body}</p>
          </Link>
        ))}
      </section>

      <RecentActivity />
    </div>
  )
}
